// StudentIdSetupModal.js — asks how a teacher identifies students and configures
// the sign-out screen accordingly (name-grid vs ID form, school-directory default,
// ID-on-return). Surfaced in context the first time a teacher reaches an
// add-students / import-classes screen without a scheme yet — not on login — via
// the useStudentIdSetup gate below. Writes the choice to the user doc as
// `studentIdScheme` so it never re-prompts; re-editable later in Settings → Sign-Out.

import React, { useState, useEffect } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { firestore } from "../shared/FirebaseConfig";
import "./StudentIdSetupModal.css";

// First-run gate, shared by every "add students" surface. Shows the prompt only
// when the teacher hasn't chosen a scheme yet and hasn't deferred it on this
// browser. Picking a scheme writes `studentIdScheme` to the user doc, which
// suppresses it everywhere; "Skip for now" sets the per-uid defer flag so the
// teacher isn't re-nagged on every visit. Mirrors useAddStudentsHelp.
const IDSCHEME_PREFIX = "classboon_idscheme_deferred";

export function useStudentIdSetup(uid, studentIdScheme) {
  const [deferred, setDeferred] = useState(false);
  const key = uid ? `${IDSCHEME_PREFIX}_${uid}` : null;

  useEffect(() => {
    if (key) setDeferred(localStorage.getItem(key) === "true");
  }, [key]);

  const deferIdSetup = () => {
    if (key) localStorage.setItem(key, "true");
    setDeferred(true);
  };

  const showIdSetup = !!uid && !studentIdScheme && !deferred;
  return { showIdSetup, deferIdSetup };
}

const OPTIONS = [
  {
    key: "none",
    icon: "🅰️",
    title: "No ID numbers",
    blurb:
      "Students tap their name from a grid. Best for younger grades who can't type an ID yet.",
  },
  {
    key: "school",
    icon: "🏫",
    title: "School / district ID numbers",
    blurb:
      "Each student has a unique school-wide ID. Shared in the school directory so other staff (art, lunch, the nurse) can sign them out too.",
  },
  {
    key: "class",
    icon: "🔢",
    title: "My own class numbers",
    blurb:
      "You number your own students (like 1–24). Kept private to your class.",
  },
];

// Map a chosen scheme to the user-doc sign-out settings.
//  none   → name-grid sign-out, no ID on return (IDs auto-assigned internally)
//  school → ID form, shared directory default, ID-on-return per the follow-up
//  class  → ID form, private roster,           ID-on-return per the follow-up
function fieldsFor(scheme, requireId) {
  if (scheme === "none") {
    return { studentIdScheme: "none", nameSignOut: true, signInRequiresId: false };
  }
  return { studentIdScheme: scheme, nameSignOut: false, signInRequiresId: !!requireId };
}

export default function StudentIdSetupModal({ uid, onClose }) {
  // While set, holds the picked ID scheme awaiting the "require ID?" follow-up.
  const [pendingScheme, setPendingScheme] = useState(null);
  const [saving, setSaving] = useState(false);

  const apply = async (scheme, requireId) => {
    setSaving(true);
    try {
      await updateDoc(doc(firestore, "users", uid), fieldsFor(scheme, requireId));
    } catch (err) {
      console.error("Error saving student-ID setup:", err);
    } finally {
      setSaving(false);
      onClose();
    }
  };

  // Name mode needs no follow-up; ID modes ask about sign-in first.
  const pick = (key) => (key === "none" ? apply("none") : setPendingScheme(key));

  return (
    <div className="idsetup-overlay" role="dialog" aria-modal="true">
      <div className="idsetup-card">
        {!pendingScheme ? (
          <>
            <h2 className="idsetup-title">How will you identify your students?</h2>
            <p className="idsetup-sub">
              This sets up your sign-out screen. You can change it anytime in
              Settings&nbsp;→&nbsp;Sign-Out.
            </p>
            <div className="idsetup-options">
              {OPTIONS.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  className="idsetup-option"
                  onClick={() => pick(o.key)}
                  disabled={saving}
                >
                  <span className="idsetup-option-icon">{o.icon}</span>
                  <span className="idsetup-option-text">
                    <span className="idsetup-option-title">{o.title}</span>
                    <span className="idsetup-option-blurb">{o.blurb}</span>
                  </span>
                </button>
              ))}
            </div>
            <button
              type="button"
              className="idsetup-skip"
              onClick={onClose}
              disabled={saving}
            >
              Skip for now
            </button>
          </>
        ) : (
          <>
            <h2 className="idsetup-title">Require ID to sign back in?</h2>
            <p className="idsetup-sub">
              When students return, should they type their ID number to sign in —
              confirming it's really them? Signing out is one tap either way.
            </p>
            <div className="idsetup-actions">
              <button
                type="button"
                className="idsetup-primary"
                onClick={() => apply(pendingScheme, true)}
                disabled={saving}
              >
                Yes — require their ID to sign in
              </button>
              <button
                type="button"
                className="idsetup-secondary"
                onClick={() => apply(pendingScheme, false)}
                disabled={saving}
              >
                No — one-tap sign in
              </button>
            </div>
            <button
              type="button"
              className="idsetup-back"
              onClick={() => setPendingScheme(null)}
              disabled={saving}
            >
              ← Back
            </button>
          </>
        )}
      </div>
    </div>
  );
}
